import type { PerformanceNotesView, PerformancePitch, PerformanceViewValue } from '@tunasong/plugin-lib'
import { partFeatures, pitchView } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isSong } from '@tunasong/schemas'
import { usePerformanceViewStore } from '../performance-settings.js'
import { usePerformanceParts } from './performance-parts.hook.js'

/**
 * How Perform renders the entity in view: the performer's parts for it (and the part choices, for the
 * menu and the prompts), and the `PerformanceViewContext` value the editor plugins read (parts, features
 * the screen's one pitch frame, and the performer's notes). Shared by the
 * song/folder Performance layout and the playlist performance view.
 */
export const usePerformanceRendering = (entity: Persisted<Entity> | undefined, notes: PerformanceNotesView) => {
  const performanceParts = usePerformanceParts(entity)
  const showChords = usePerformanceViewStore(state => state.showChords)
  const { parts, frame } = performanceParts
  const song = isSong(entity) ? entity : undefined
  /** Absent capo means 0 and absent chordsWrittenAs means shapes, resolved here (Global Constraints). */
  const pitch: PerformancePitch = {
    capo: song?.capo ?? 0,
    chordsWrittenAs: song?.chordsWrittenAs ?? 'shapes',
    writtenKey: song?.scale ?? undefined,
  }
  const view: PerformanceViewValue = {
    parts,
    features: partFeatures(parts, { showChords }),
    pitch,
    view: pitchView(parts, { capo: pitch.capo, frame, showChords }),
    notes,
  }
  return { performanceParts, view }
}
